import type { Database } from '@planmax/shared-types'
import type { BadgeProps } from '@/components/ui/badge'
import type { RecordStatusFilter } from '@/types/record-status'

// ============================================
// Enums from Database
// ============================================

export type FuelType = Database['public']['Enums']['fuel_type']
export type FuelPricingType = Database['public']['Enums']['fuel_pricing_type']
export type FuelTransactionSource = Database['public']['Enums']['fuel_transaction_source']
export type FuelTransactionStatus = Database['public']['Enums']['fuel_transaction_status']
export type FuelPriceType = Database['public']['Enums']['fuel_price_type']

// ============================================
// Enum Arrays for Iteration
// ============================================

export const FUEL_TYPES = ['jet_a', 'avgas'] as const

export const FUEL_PRICING_TYPES = [
  'contract',
  'retail',
  'contract_fuel_network',
  'custom',
] as const

export const FUEL_SOURCES = ['manual', 'csv_import', 'pos_sync', 'api'] as const

export const FUEL_STATUSES = ['pending', 'completed', 'voided', 'disputed'] as const

export const FUEL_PRICE_TYPES = ['fixed', 'opis_plus', 'retail_discount'] as const

// ============================================
// Enum Labels
// ============================================

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  jet_a: 'Jet-A',
  avgas: '100LL Avgas',
}

export const FUEL_PRICING_TYPE_LABELS: Record<FuelPricingType, string> = {
  contract: 'Contract Rate',
  retail: 'Retail',
  contract_fuel_network: 'Contract Fuel Network',
  custom: 'Custom',
}

export const FUEL_SOURCE_LABELS: Record<FuelTransactionSource, string> = {
  manual: 'Manual Entry',
  csv_import: 'CSV Import',
  pos_sync: 'POS Sync',
  api: 'API',
}

export const FUEL_STATUS_LABELS: Record<FuelTransactionStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  voided: 'Voided',
  disputed: 'Disputed',
}

export const FUEL_PRICE_TYPE_LABELS: Record<FuelPriceType, string> = {
  fixed: 'Fixed Price',
  opis_plus: 'OPIS Plus',
  retail_discount: 'Retail Discount',
}

// ============================================
// Badge Variants for Status
// ============================================

type BadgeVariant = BadgeProps['variant']

export const FUEL_STATUS_VARIANTS: Record<FuelTransactionStatus, BadgeVariant> = {
  pending: 'secondary',
  completed: 'default',
  voided: 'destructive',
  disputed: 'outline',
}

export const FUEL_SOURCE_VARIANTS: Record<FuelTransactionSource, BadgeVariant> = {
  manual: 'secondary',
  csv_import: 'outline',
  pos_sync: 'default',
  api: 'default',
}

// ============================================
// Table Row Type (with joined data)
// ============================================

export interface FuelTransactionTableRow {
  // Core
  id: string
  organization_id: string
  location_id: string
  transaction_number: string

  // Parties (with joined names)
  client_id: string
  client_name: string | null
  aircraft_id: string
  tail_number: string | null
  aircraft_type_name: string | null
  lease_contract_id: string | null
  contract_number: string | null
  transient_stay_id: string | null
  booking_number: string | null

  // Location (with joined data)
  location_name: string | null

  // Transaction details
  fuel_type: FuelType
  quantity_gallons: number
  price_per_gallon: number
  total_amount: number
  pricing_type: FuelPricingType | null

  // When & Where
  fueled_at: string
  truck_id: string | null
  fueler_name: string | null

  // Meter data
  meter_start: number | null
  meter_end: number | null

  // Integration
  source: FuelTransactionSource
  external_reference: string | null
  external_system: string | null

  // Status
  status: FuelTransactionStatus
  notes: string | null

  // Audit
  created_at: string
  updated_at: string | null
  deleted_at: string | null
}

// ============================================
// Form Data Type
// ============================================

export interface FuelTransactionFormData {
  // Required fields
  location_id: string
  client_id: string
  aircraft_id: string
  fuel_type: FuelType
  quantity_gallons: number
  price_per_gallon: number
  fueled_at: string

  // Optional fields
  lease_contract_id?: string | null
  transient_stay_id?: string | null
  pricing_type?: FuelPricingType | null
  truck_id?: string | null
  fueler_name?: string | null
  meter_start?: number | null
  meter_end?: number | null
  source?: FuelTransactionSource
  external_reference?: string | null
  external_system?: string | null
  status?: FuelTransactionStatus
  notes?: string | null
}

// ============================================
// Filter Type
// ============================================

export interface FuelTransactionFilters {
  search?: string
  fuelType?: FuelType[]
  status?: FuelTransactionStatus[]
  source?: FuelTransactionSource[]
  locationId?: string
  aircraftId?: string
  clientId?: string
  contractId?: string
  dateFrom?: string
  dateTo?: string
  recordStatus?: RecordStatusFilter
}

// ============================================
// Aggregation Types (for FuelTab and reports)
// ============================================

/**
 * Summary of fuel transactions for a single aircraft
 * Used in the FuelTab of the ExpandingCard
 */
export interface AircraftFuelSummary {
  aircraftId: string
  tailNumber: string | null

  // Commitment info (from lease contract)
  hasCommitment: boolean
  commitmentGallonsMonthly: number | null
  commitmentPricePerGallon: number | null

  // Monthly totals
  monthlyGallons: number
  monthlyRevenue: number
  monthlyTransactionCount: number

  // YTD totals
  ytdGallons: number
  ytdRevenue: number
  ytdTransactionCount: number

  // Recent transactions (last 5)
  recentTransactions: FuelTransactionTableRow[]
}

/**
 * Progress tracking for fuel commitments
 * Used for commitment status dashboards
 */
export interface FuelCommitmentProgress {
  contractId: string
  contractNumber: string
  clientId: string
  clientName: string | null
  tailNumber: string | null

  // Commitment
  committedGallons: number
  deliveredGallons: number
  remainingGallons: number
  percentComplete: number

  // Time remaining
  daysRemaining: number
  periodStart: string
  periodEnd: string

  // Status
  isAtRisk: boolean // less than 70% with 10 days remaining
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format fuel quantity with commas and "gal" suffix
 */
export function formatFuelQuantity(gallons: number | null): string {
  if (gallons === null) return '-'
  return `${gallons.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} gal`
}

/**
 * Format price per gallon with 4 decimal places
 */
export function formatPricePerGallon(price: number | null): string {
  if (price === null) return '-'
  return `$${price.toFixed(4)}/gal`
}

/**
 * Format fuel total as currency
 */
export function formatFuelTotal(amount: number | null): string {
  if (amount === null) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Calculate total amount from gallons and price
 */
export function calculateFuelTotal(gallons: number, pricePerGallon: number): number {
  return Math.round(gallons * pricePerGallon * 100) / 100
}

/**
 * Format fuel datetime for display
 */
export function formatFueledAt(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Format fuel date only
 */
export function formatFuelDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Calculate commitment progress percentage
 */
export function calculateCommitmentProgress(
  delivered: number,
  committed: number
): number {
  if (committed <= 0) return 0
  return Math.min(100, Math.round((delivered / committed) * 100))
}

/**
 * Check if commitment is at risk
 * At risk = less than 70% complete with 10 or fewer days remaining
 */
export function isCommitmentAtRisk(
  percentComplete: number,
  daysRemaining: number
): boolean {
  return percentComplete < 70 && daysRemaining <= 10
}

/**
 * Get days remaining in current month
 */
export function getDaysRemainingInMonth(): number {
  const now = new Date()
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return lastDay.getDate() - now.getDate()
}

/**
 * Get display name for fuel transaction
 */
export function getFuelTransactionDisplayName(
  transaction: Pick<FuelTransactionTableRow, 'transaction_number' | 'tail_number' | 'quantity_gallons'>
): string {
  const parts = [transaction.transaction_number]
  if (transaction.tail_number) parts.push(transaction.tail_number)
  parts.push(`${transaction.quantity_gallons} gal`)
  return parts.join(' - ')
}

/**
 * Validate meter readings (end should be greater than start)
 */
export function validateMeterReadings(
  meterStart: number | null,
  meterEnd: number | null,
  quantityGallons: number
): { valid: boolean; message?: string } {
  if (meterStart === null || meterEnd === null) {
    return { valid: true }
  }

  if (meterEnd <= meterStart) {
    return { valid: false, message: 'Meter end must be greater than meter start' }
  }

  const meterDiff = meterEnd - meterStart
  const tolerance = quantityGallons * 0.05 // 5% tolerance

  if (Math.abs(meterDiff - quantityGallons) > tolerance) {
    return {
      valid: false,
      message: `Meter difference (${meterDiff.toFixed(2)}) doesn't match quantity (${quantityGallons})`,
    }
  }

  return { valid: true }
}
